import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { 
  Search, Home, Sparkles, Tag, Settings, User, 
  Heart, BarChart3, Bot, Zap, BookOpen, Code,
  Palette, Video, Music, Briefcase
} from 'lucide-react';

interface CommandPaletteProps {
  onSearch?: (query: string) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ onSearch }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = (command: () => void) => {
    setOpen(false);
    command();
  };

  const quickSearches = [
    { label: 'Writing & Content', icon: BookOpen, query: 'writing content' },
    { label: 'Coding & Development', icon: Code, query: 'coding developer' },
    { label: 'Image Generation', icon: Palette, query: 'image generation' },
    { label: 'Video Creation', icon: Video, query: 'video creation' },
    { label: 'Music & Audio', icon: Music, query: 'music audio' },
    { label: 'Business & Productivity', icon: Briefcase, query: 'business productivity' },
  ];

  const navigationItems = [
    { label: 'Home', icon: Home, path: '/' },
    { label: 'AI Assistant', icon: Sparkles, path: '/ai-recommendations' },
    { label: 'Deals', icon: Tag, path: '/deals' },
    { label: 'Compare', icon: BarChart3, path: '/compare' },
    { label: 'Profile', icon: User, path: '/profile' },
    { label: 'Settings', icon: Settings, path: '/settings' },
  ];

  return (
    <>
      {/* Keyboard shortcut hint */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground 
                   glass rounded-lg hover:bg-accent transition-colors"
      >
        <Search className="w-4 h-4" />
        <span>Search...</span>
        <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 
                       rounded border border-border bg-muted px-1.5 font-mono text-[10px] 
                       font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search AI tools, navigate, or type a command..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          
          <CommandGroup heading="Quick Search">
            {quickSearches.map((item) => (
              <CommandItem
                key={item.label}
                onSelect={() => runCommand(() => {
                  onSearch?.(item.query);
                  navigate('/');
                })}
              >
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Navigation">
            {navigationItems.map((item) => (
              <CommandItem
                key={item.path}
                onSelect={() => runCommand(() => navigate(item.path))}
              >
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Actions">
            <CommandItem onSelect={() => runCommand(() => navigate('/submit-bot'))}>
              <Bot className="mr-2 h-4 w-4" />
              <span>Submit a Bot</span>
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => navigate('/credits'))}>
              <Zap className="mr-2 h-4 w-4" />
              <span>View Credits</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default CommandPalette;
